import { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { RequireRole } from "@/lib/useUser";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, XCircle } from "lucide-react";

const BUYER_TIERS = [
  { value: "essential", label: "Essential" },
  { value: "essentialPlus", label: "Essential Plus" },
  { value: "acquisitionPro", label: "Acquisition Pro" },
  { value: "byod", label: "BYOD" },
];

const CONSULTANT_TIERS = [
  { value: "consultant_explorer", label: "Explorer" },
  { value: "consultant_pro", label: "Pro" },
  { value: "consultant_elite", label: "Elite" },
];

const PARTNER_TIERS = [
  { value: "basic", label: "Basic" },
  { value: "pro", label: "Pro" },
  { value: "elite", label: "Elite" },
];

const PARTNER_ADDONS = [
  { value: "business_law", label: "Business Law" },
  { value: "immigration_law", label: "Immigration Law" },
  { value: "notary", label: "Notary" },
  { value: "cpa", label: "CPA" },
  { value: "tax_advisor", label: "Tax Advisor" },
];

const ROLES_WITH_TIER = ["buyer", "consultant", "partner"];

export default function AdminSetRolePage() {
  const [uid, setUid] = useState("");
  const [role, setRole] = useState("buyer");
  const [tier, setTier] = useState("");
  const [addons, setAddons] = useState<string[]>([]);
  const [founding, setFounding] = useState(false);
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleRoleChange = (newRole: string) => {
    setRole(newRole);
    setTier("");
    setAddons([]);
  };

  const toggleAddon = (value: string) => {
    setAddons((prev) =>
      prev.includes(value) ? prev.filter((a) => a !== value) : [...prev, value]
    );
  };

  const tierOptions =
    role === "buyer" ? BUYER_TIERS : role === "consultant" ? CONSULTANT_TIERS : role === "partner" ? PARTNER_TIERS : [];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setLoading(true);
    try {
      const payload: Record<string, any> = { uid, role };
      if (ROLES_WITH_TIER.includes(role)) {
        if (!tier) {
          throw new Error("Merci de sélectionner un tier pour ce rôle.");
        }
        payload.tier = tier;
      }
      if (role === "partner" && addons.length > 0) {
        payload.addons = addons;
      }
      if (founding) {
        payload.founding = true;
      }

      const fn = httpsCallable(getFunctions(), "setUserRole");
      await fn(payload);
      setMsg({
        type: "success",
        text: `Successfully applied role "${role}"${tier ? ` (tier: ${tier})` : ""} to user ${uid}`,
      });
      setUid("");
      setTier("");
      setAddons([]);
      setFounding(false);
    } catch (e: any) {
      setMsg({ type: "error", text: e.message || "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <RequireRole allowed={["admin", "staff"]} fallback={<div className="p-6 text-center">Access denied</div>}>
      <div className="container max-w-2xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Admin — Set User Role</CardTitle>
            <CardDescription>Assign roles, tiers, and add-ons to users in the system</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="uid">User UID</Label>
                <Input
                  id="uid"
                  value={uid}
                  onChange={(e) => setUid(e.target.value)}
                  placeholder="Paste the UID from Firebase Auth"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={role} onValueChange={handleRoleChange}>
                  <SelectTrigger id="role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="seller">Seller</SelectItem>
                    <SelectItem value="broker">Broker</SelectItem>
                    <SelectItem value="lender">Lender</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="partner">Partner (Our Experts)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ── Tier — Buyer / Consultant / Partner uniquement ── */}
              {ROLES_WITH_TIER.includes(role) && (
                <div className="space-y-2">
                  <Label htmlFor="tier">
                    {role === "buyer" && "Buyer Plan"}
                    {role === "consultant" && "Consultant Plan"}
                    {role === "partner" && "Partner Plan"}
                  </Label>
                  <Select value={tier} onValueChange={setTier}>
                    <SelectTrigger id="tier">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {tierOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* ── Add-ons — Partner uniquement ── */}
              {role === "partner" && (
                <div className="space-y-2">
                  <Label>Add-ons (+$49/mo each)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {PARTNER_ADDONS.map((addon) => (
                      <label
                        key={addon.value}
                        className="flex items-center gap-2 text-sm border rounded-md px-3 py-2 cursor-pointer hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={addons.includes(addon.value)}
                          onCheckedChange={() => toggleAddon(addon.value)}
                        />
                        {addon.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Founding member — tous rôles avec tier ── */}
              {ROLES_WITH_TIER.includes(role) && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={founding} onCheckedChange={(v) => setFounding(v === true)} />
                  Founding Member (Early Access)
                </label>
              )}

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Updating..." : "Apply Role"}
              </Button>
            </form>

            {msg && (
              <Alert className="mt-6" variant={msg.type === "error" ? "destructive" : "default"}>
                {msg.type === "success" ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <AlertDescription>{msg.text}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </RequireRole>
  );
}